import React, { useEffect, useMemo, useState } from "react";
import { cleanCoCoName } from "../helpers/cocoNameHelper";
import { Link, useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight, FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { FiChevronRight } from "react-icons/fi";
import { useTranslation } from "../context/TranslationContext";
import {
  GLOBAL_SEARCH_TYPES,
  buildClientRecordPath,
  formatPostalCode,
  formatSearchAddress,
  formatSearchDocument,
  getCaseTabBadgeClass,
  getCaseTypeLabel,
  getMatterStatusKey,
  getResultMatchedOn,
  getResultMatterNumber,
} from "../helpers/globalSearchHelper";
import SearchHighlight from "./SearchHighlight";

const EMPTY = "-";
export const RESULTS_PAGE_SIZES = [25, 50, 100];
export const DEFAULT_RESULTS_PAGE_SIZE = 25;

const translateOr = (t, key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};

const getStatusLabel = (t, result) => {
  const statusKey = getMatterStatusKey(result?.matterStatus);
  return statusKey ? t(`clientsTable.matterStatuses.${statusKey}`) : result?.matterStatus || "";
};

const getCityState = (address) =>
  [address?.city, address?.state].filter(Boolean).join("/");

const SORT_ACCESSORS = {
  name: (result) => result?.partyName,
  document: (result) => String(result?.document ?? "").replace(/\D/g, ""),
  matterNumber: (result) => getResultMatterNumber(result),
  location: (result) => getCityState(result?.address),
  email: (result) => result?.email,
  cocoName: (result) => cleanCoCoName(result?.cocoName),
  caseType: (result, t) => getCaseTypeLabel(result?.caseType, t),
  status: (result, t) => getStatusLabel(t, result),
};

const COLUMNS = [
  { key: "name", labelKey: "globalSearch.table.name" },
  { key: "document", labelKey: "globalSearch.table.document" },
  { key: "matterNumber", labelKey: "globalSearch.table.matterNumber" },
  { key: "location", labelKey: "globalSearch.table.location" },
  { key: "email", labelKey: "globalSearch.table.email" },
  { key: "cocoName", labelKey: "globalSearch.table.coCounsel" },
  { key: "caseType", labelKey: "globalSearch.table.caseType" },
  { key: "status", labelKey: "globalSearch.table.status" },
];

const compareValues = (a, b) => {
  const left = String(a ?? "");
  const right = String(b ?? "");
  // Empty values always sink to the bottom.
  if (!left && right) return 1;
  if (left && !right) return -1;
  return left.localeCompare(right, "pt-BR", { sensitivity: "base", numeric: true });
};

export const sortGlobalSearchResults = (results, sortConfig, t = (key) => key) => {
  const accessor = SORT_ACCESSORS[sortConfig?.key];
  if (!accessor) return results;
  const direction = sortConfig.direction === "desc" ? -1 : 1;
  return results
    .map((result, index) => ({ result, index }))
    .sort((a, b) => {
      const left = accessor(a.result, t);
      const right = accessor(b.result, t);
      if (!left !== !right) return compareValues(left, right);
      return compareValues(left, right) * direction || a.index - b.index;
    })
    .map(({ result }) => result);
};

const defaultRecordState = () => ({ activePortal: "coordinator" });

export const GlobalSearchResultRow = ({
  result,
  query,
  highlightQueries = null,
  getRecordPath = buildClientRecordPath,
  getRecordState = defaultRecordState,
  hideCoCounsel = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const recordPath = getRecordPath(result);
  const recordState = getRecordState(result);
  // Advanced search: every filled criterion matched, so highlight per kind.
  const matchedOn = highlightQueries ? [] : getResultMatchedOn(result, query);
  const needleFor = (kind) => (highlightQueries ? highlightQueries[kind] || "" : query);
  const matches = (kind) => (highlightQueries ? Boolean(highlightQueries[kind]) : matchedOn.includes(kind));
  const documentType = result?.documentType === "CNPJ" ? "CNPJ" : "CPF";
  const document = formatSearchDocument(result?.document, result?.documentType);
  const matterNumber = getResultMatterNumber(result);
  const cityState = getCityState(result?.address);
  const postalCode = formatPostalCode(result?.address?.postalCode);
  const fullAddress = formatSearchAddress(result?.address);
  const status = getStatusLabel(t, result);
  const caseLabel = result?.caseTab
    ? translateOr(t, `globalSearch.caseTabs.${result.caseTab}`, result.caseTab)
    : null;
  const openRecord = () => {
    if (recordPath) navigate(recordPath, { state: recordState });
  };

  return (
    <tr
      className={`global-search-row${recordPath ? " is-clickable" : ""}`}
      data-testid="global-search-row"
      tabIndex={recordPath ? 0 : undefined}
      title={recordPath ? undefined : t("globalSearch.card.recordUnavailable")}
      onClick={openRecord}
      onKeyDown={(event) => {
        if (event.key === "Enter" && event.target === event.currentTarget) {
          event.preventDefault();
          openRecord();
        }
      }}
    >
      <td data-label={t("globalSearch.table.name")} className="global-search-name-cell">
        <span className="global-search-name">
          <strong className="global-search-name-text" title={result?.partyName || undefined}>
            <SearchHighlight
              text={result?.partyName || EMPTY}
              query={needleFor(GLOBAL_SEARCH_TYPES.NAME)}
              enabled={matches(GLOBAL_SEARCH_TYPES.NAME)}
            />
          </strong>
          {(caseLabel || result?.business) && (
            <span className="global-search-badges">
              {caseLabel && (
                <span className={`case-badge ${getCaseTabBadgeClass(result.caseTab)}`}>
                  {caseLabel}
                </span>
              )}
              {result?.business && (
                <span className="business-badge business-badge-headquarters">
                  {t("globalSearch.card.business")}
                </span>
              )}
            </span>
          )}
        </span>
      </td>
      <td data-label={t("globalSearch.table.document")} className="global-search-nowrap global-search-compact-cell">
        {document ? (
          <span title={t(`globalSearch.card.document.${documentType}`)}>
            <SearchHighlight
              text={document}
              query={needleFor(documentType)}
              enabled={matches(GLOBAL_SEARCH_TYPES.CPF) || matches(GLOBAL_SEARCH_TYPES.CNPJ)}
            />
          </span>
        ) : EMPTY}
      </td>
      <td data-label={t("globalSearch.table.matterNumber")} className="global-search-nowrap">
        {matterNumber || result?.masterSchedule ? (
          <span className="global-search-location">
            {matterNumber ? (
              <SearchHighlight
                text={matterNumber}
                query={needleFor(GLOBAL_SEARCH_TYPES.MATTER)}
                enabled={matches(GLOBAL_SEARCH_TYPES.MATTER)}
              />
            ) : EMPTY}
            {result?.masterSchedule && (
              <span className="global-search-subtle" title={t("advancedSearchForm.fields.masterSchedule")}>
                <SearchHighlight
                  text={result.masterSchedule}
                  query={needleFor("MASTER_SCHEDULE")}
                  enabled={matches("MASTER_SCHEDULE")}
                />
              </span>
            )}
          </span>
        ) : EMPTY}
      </td>
      <td data-label={t("globalSearch.table.location")}>
        {cityState || postalCode ? (
          <span className="global-search-location" title={fullAddress || undefined}>
            <span className="global-search-ellipsis">
              {cityState ? (
                <SearchHighlight
                  text={cityState}
                  query={needleFor("CITY")}
                  enabled={matches("CITY")}
                />
              ) : EMPTY}
            </span>
            {postalCode && (
              <span className="global-search-subtle">
                CEP{" "}
                <SearchHighlight
                  text={postalCode}
                  query={needleFor(GLOBAL_SEARCH_TYPES.POSTCODE)}
                  enabled={matches(GLOBAL_SEARCH_TYPES.POSTCODE)}
                />
              </span>
            )}
          </span>
        ) : EMPTY}
      </td>
      <td data-label={t("globalSearch.table.email")}>
        {result?.email ? (
          <span className="global-search-ellipsis" title={result.email}>
            <SearchHighlight
              text={result.email}
              query={needleFor(GLOBAL_SEARCH_TYPES.EMAIL)}
              enabled={matches(GLOBAL_SEARCH_TYPES.EMAIL)}
            />
          </span>
        ) : EMPTY}
      </td>
      {!hideCoCounsel && (
        <td data-label={t("globalSearch.table.coCounsel")}>
          {result?.cocoName ? (
            <span className="global-search-ellipsis" title={cleanCoCoName(result.cocoName)}>
              {cleanCoCoName(result.cocoName)}
            </span>
          ) : (
            <em
              className="global-search-muted global-search-ellipsis"
              title={t("globalSearch.card.noCoCounsel")}
            >
              {t("globalSearch.card.noCoCounsel")}
            </em>
          )}
        </td>
      )}
      <td data-label={t("globalSearch.table.caseType")} className="global-search-compact-cell global-search-casetype-cell">
        {getCaseTypeLabel(result?.caseType, t) || EMPTY}
      </td>
      {/* Terminated is shown here rather than as a name badge (saves a row line). */}
      <td
        data-label={t("globalSearch.table.status")}
        className={`global-search-compact-cell global-search-status-cell${result?.terminated ? " global-search-status-terminated" : ""}`}
      >
        {status || (result?.terminated ? t("globalSearch.card.terminated") : EMPTY)}
      </td>
      <td data-label={t("globalSearch.card.viewRecord")} className="global-search-action-cell">
        {recordPath ? (
          <Link
            className="global-search-open-link"
            to={recordPath}
            state={recordState}
            tabIndex={-1}
            aria-label={t("globalSearch.card.viewRecord")}
            title={t("globalSearch.card.viewRecord")}
            onClick={(event) => event.stopPropagation()}
          >
            <FiChevronRight aria-hidden="true" />
          </Link>
        ) : (
          <span className="global-search-open-link is-disabled" aria-hidden="true">
            <FiChevronRight />
          </span>
        )}
      </td>
    </tr>
  );
};

const GlobalSearchResultsTable = ({
  results,
  query,
  highlightQueries = null,
  getRecordPath,
  getRecordState,
  hideCoCounsel = false,
  summary = null,
}) => {
  const { t } = useTranslation();
  const columns = hideCoCounsel ? COLUMNS.filter(({ key }) => key !== "cocoName") : COLUMNS;
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [pageSize, setPageSize] = useState(DEFAULT_RESULTS_PAGE_SIZE);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [results, pageSize, sortConfig]);

  const sorted = useMemo(
    () => sortGlobalSearchResults(results, sortConfig, t),
    [results, sortConfig, t],
  );
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageResults = sorted.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSort = (key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort aria-hidden="true" />;
    return sortConfig.direction === "asc"
      ? <FaSortUp aria-hidden="true" />
      : <FaSortDown aria-hidden="true" />;
  };

  return (
    <>
      <div className="global-search-table-toolbar">
        {summary}
        <div className="page-selector">
          <label htmlFor="global-search-page-size">{t("clientsTable.pageSize")}</label>
          <select
            id="global-search-page-size"
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value))}
          >
            {RESULTS_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="client-table-shell global-search-table-shell">
        <div className="client-table-scroll">
          <table className="client-table global-search-table">
            <thead>
              <tr>
                {columns.map(({ key, labelKey }) => (
                  <th
                    key={key}
                    scope="col"
                    aria-sort={
                      sortConfig.key === key
                        ? (sortConfig.direction === "asc" ? "ascending" : "descending")
                        : "none"
                    }
                  >
                    <button
                      type="button"
                      className="table-sort-button"
                      onClick={() => handleSort(key)}
                    >
                      <span>{t(labelKey)}</span>
                      {renderSortIcon(key)}
                    </button>
                  </th>
                ))}
                <th scope="col" className="global-search-action-column">
                  <span className="sr-only">{t("globalSearch.card.viewRecord")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {pageResults.map((result, index) => (
                <GlobalSearchResultRow
                  key={`${result?.matterId || "matter"}-${result?.partyId || "party"}-${index}`}
                  result={result}
                  query={query}
                  highlightQueries={highlightQueries}
                  getRecordPath={getRecordPath}
                  getRecordState={getRecordState}
                  hideCoCounsel={hideCoCounsel}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="pagination-container">
        <button
          type="button"
          className="pagination-button"
          onClick={() => setPage(Math.max(currentPage - 1, 1))}
          disabled={currentPage === 1}
        >
          <FaChevronLeft aria-hidden="true" />
          <span>{t("clientsTable.previous")}</span>
        </button>
        <span className="pagination-info">
          {t("clientsTable.page")} <strong>{currentPage}</strong>{" "}
          {t("clientsTable.of")} <strong>{totalPages}</strong>
        </span>
        <button
          type="button"
          className="pagination-button"
          onClick={() => setPage(Math.min(currentPage + 1, totalPages))}
          disabled={currentPage === totalPages}
        >
          <span>{t("clientsTable.next")}</span>
          <FaChevronRight aria-hidden="true" />
        </button>
      </div>
    </>
  );
};

export default GlobalSearchResultsTable;
